import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import Colors from '../../../../ui/global/Colors';
import Fonts from '../../../../ui/global/Fonts';
import { createMentionPillElement, Mention, MentionPillStyles } from './MentionPill';

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  max-width: 100%;
`;

const StyledInput = styled.div<{ $colorMode: 'light' | 'dark' }>`
  width: 100%;
  min-width: 150px;
  max-width: 100%;
  min-height: 1.2em;
  border: none;
  outline: none;
  color: ${props => props.$colorMode === 'dark' ? Colors.white : Colors.black};
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  font-size: inherit;
  text-align: left;
  line-height: 1.2em;
  padding: 6px 12px;
  white-space: pre-wrap;
  text-wrap: stable;
  overflow: hidden;
  box-sizing: border-box;
  cursor: text;
  background-color: ${Colors.transparent};
  border-bottom: 1px solid ${Colors.transparent};
  transition: border 0.2s ease-in-out;

  &:hover {
    border-bottom: 1px solid ${Colors.transparent};
  }

  &:focus {
    border-bottom: 1px solid ${Colors.transparent};
  }

  &:empty:before {
    content: attr(data-placeholder);
    color: ${Colors.grey6};
  }
`;

interface InputWithMentionsProps {
  value: string;
  onChange: (value: string, mentions: Mention[]) => void;
  placeholder?: string;
  colorMode?: 'light' | 'dark';
  onAtMenu: (open: boolean, atPosition: { x: number, y: number, width: number, height: number }) => void;
  onMentionCreated?: (mention: Mention) => void;
  onMentionRemoved?: (mention: Mention) => void;
  onMentionClicked?: (mention: Mention) => void;
  onEnterPress?: () => void;
  onCurrentMentionChange: (currentMention: string | null, type: string | null, filter: string) => void;
}

export interface InputWithMentionsRef {
  focus: () => void;
  insertMention: (mention: Mention) => void;
  insertTypeSpecifier: (type: string) => void;
}

interface CursorContext {
  isAtMention: boolean;
  isTypeSpecified: boolean;
  type: string | null;
  filter: string;
  position: { x: number, y: number, width: number, height: number } | null;
  fullMentionText: string | null;
}

const InputWithMentions = forwardRef<InputWithMentionsRef, InputWithMentionsProps>(({
  value,
  onChange,
  placeholder = '',
  colorMode = 'light',
  onAtMenu,
  onMentionCreated,
  onMentionRemoved,
  onMentionClicked,
  onEnterPress,
  onCurrentMentionChange,
}, ref) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [isComposing, setIsComposing] = useState(false);
  const [cursorContext, setCursorContext] = useState<CursorContext>({
    isAtMention: false,
    isTypeSpecified: false,
    type: null,
    filter: '',
    position: null,
    fullMentionText: null
  });
  const [lastSelection, setLastSelection] = useState<Range | null>(null);

  // Helper function to convert HTML to our text format
  const convertHtmlToValue = useCallback((html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return Array.from(temp.childNodes).map(node => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        if (el.classList.contains('mention-pill')) {
          return `@${el.dataset.type}:${el.dataset.name}[${el.dataset.id}]`;
        }
      }
      return '';
    }).join('');
  }, []);

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
    insertMention: (mention: Mention) => {
      handleInsertMention(mention);
    },
    insertTypeSpecifier: (type: string) => {
      insertTypeSpecifier(type);
    }
  }));
  
  // Parse the value to extract mentions
  useEffect(() => {
    const mentionRegex = /@([a-zA-Z0-9_]+):([^\]]+)\[([a-f0-9-]+)\]/g;
    const newMentions: Mention[] = [];
    let match;
    
    while ((match = mentionRegex.exec(value)) !== null) {
      newMentions.push({
        id: match[3],
        type: match[1],
        name: match[2].trim(),
      });
    }
    
    setMentions(newMentions);
  }, [value]);

  // Update the DOM to reflect the current value and render mention pills
  useEffect(() => {
    if (!inputRef.current) return;

    // Skip rebuild if the content hasn't changed
    const currentHtml = inputRef.current.innerHTML;
    const currentValue = convertHtmlToValue(currentHtml);
    if (currentValue === value) return;

    // Store selection
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const startOffset = range?.startOffset ?? 0;
    const startContainer = range?.startContainer ?? null;
    
    // Clear and rebuild content
    inputRef.current.innerHTML = '';
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;
    const mentionRegex = /@([a-zA-Z0-9_]+):([^\]]+)\[([a-f0-9-]+)\]/g;
    const matches = Array.from(value.matchAll(mentionRegex));

    if (matches.length === 0) {
      fragment.appendChild(document.createTextNode(value));
    } else {
      matches.forEach((match, i) => {
        const [fullMatch, type, name, id] = match;
        const matchIndex = match.index ?? 0;
        
        // Add text before mention
        if (matchIndex > lastIndex) {
          fragment.appendChild(
            document.createTextNode(value.substring(lastIndex, matchIndex))
          );
        }
        
        // Add mention pill with trimmed name
        fragment.appendChild(
          createMentionPillElement({ id, type, name: name.trim() }, onMentionClicked)
        );
        
        lastIndex = matchIndex + fullMatch.length;
        
        // Add remaining text after last mention
        if (i === matches.length - 1 && lastIndex < value.length) {
          fragment.appendChild(
            document.createTextNode(value.substring(lastIndex))
          );
        }
      });
    }

    inputRef.current.appendChild(fragment);
    
    // Restore selection
    if (selection && startContainer) {
      try {
        const nodeIterator = document.createNodeIterator(
          inputRef.current,
          NodeFilter.SHOW_TEXT
        );
        
        let currentNode;
        let found = false;
        
        while ((currentNode = nodeIterator.nextNode())) {
          if (currentNode.textContent === startContainer.textContent) {
            const newRange = document.createRange();
            newRange.setStart(currentNode, Math.min(startOffset, currentNode.textContent?.length || 0));
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
            found = true;
            break;
          }
        }
        
        if (!found && inputRef.current.lastChild) {
          const newRange = document.createRange();
          if (inputRef.current.lastChild.nodeType === Node.TEXT_NODE) {
            newRange.setStart(inputRef.current.lastChild, inputRef.current.lastChild.textContent?.length || 0);
          } else {
            newRange.setStartAfter(inputRef.current.lastChild);
          }
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);
        }
      } catch (e) {
        console.error('Error restoring selection:', e);
      }
    }

    analyzeCursorContext();
  }, [value, onMentionClicked, convertHtmlToValue]);

  // Analyze cursor context for mentions
  // TEST CASES:
  // @
  // @art
  // @sound
  // @art:
  // @art:name
  // @art:name[id]
  // @art:name with spaces[id]
  // NEGATIVE TEST CASES:
  // @ something --> only the @ is part of the mention, not the something
  // @art:name something --> only the @art:name is part of the mention, since there is no [id] after the spaces
  const analyzeCursorContext = useCallback(() => {
    if (!inputRef.current) return;
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      if (cursorContext.isAtMention) {
        resetCursorContext();
      }
      return;
    }
    
    const range = selection.getRangeAt(0);
    setLastSelection(range.cloneRange());
    
    if (!range.collapsed) {
      if (cursorContext.isAtMention) {
        resetCursorContext();
      }
      return;
    }
    
    const cursorNode = range.startContainer;
    const cursorOffset = range.startOffset;
    
    // Get text before and after cursor
    let textBeforeCursor = '';
    let textAfterCursor = '';
    if (cursorNode.nodeType === Node.TEXT_NODE) {
      textBeforeCursor = cursorNode.textContent?.substring(0, cursorOffset) || '';
      textAfterCursor = cursorNode.textContent?.substring(cursorOffset) || '';
    }
    // console.log('textBeforeCursor', textBeforeCursor);
    // console.log('textAfterCursor', textAfterCursor);
    
    // Check for @ mention before cursor
    const atIndex = textBeforeCursor.lastIndexOf('@');
    if (atIndex === -1) {
      if (cursorContext.isAtMention) {
        resetCursorContext();
      }
      return;
    }
    
    // Get the text from @ to end of current text node
    const mentionTextInNode = textBeforeCursor.substring(atIndex) + textAfterCursor;
    // console.log('mentionTextInNode', mentionTextInNode);
    
    // Check if we're at a single @ (no text after, or whitespace follows)
    const isSingleAt = textBeforeCursor.length === atIndex + 1 && 
      (textAfterCursor.length === 0 || /^\s/.test(textAfterCursor));
    
    // Check if there's whitespace between @ and cursor (negative case)
    const textAfterAt = textBeforeCursor.substring(atIndex + 1, cursorOffset);
    if (textAfterAt.length > 0 && /\s/.test(textAfterAt)) {
      if (cursorContext.isAtMention) {
        resetCursorContext();
      }
      return;
    }
    
    // Now match the full mention pattern
    const mentionMatch = mentionTextInNode.match(/^(@[^\s@:]+(?::[^\s@]+)?(?:\[[^\]]+\])?)/);
    // console.log('mentionMatch', mentionMatch);
    
    // Determine if we're actually in a mention
    const isAtMention = isSingleAt || (mentionMatch && mentionMatch[0].length > 0);
    // console.log('isAtMention', isAtMention);
    if (!isAtMention) {
      if (cursorContext.isAtMention) {
        resetCursorContext();
      }
      return;
    }
    
    const fullMentionText = isSingleAt ? '@' : (mentionMatch ? mentionMatch[0] : '');
    // console.log('fullMentionText', fullMentionText);
    
    // Get position of @
    let atPosition = null;
    if (cursorNode.nodeType === Node.TEXT_NODE) {
      const atRange = document.createRange();
      atRange.setStart(cursorNode, atIndex);
      atRange.setEnd(cursorNode, atIndex + 1);
      atPosition = atRange.getBoundingClientRect();
    }
    // console.log('atPosition', atPosition);
    
    const colonIndex = fullMentionText.indexOf(':');
    const isTypeSpecified = colonIndex !== -1;
    
    const type = isTypeSpecified ? fullMentionText.substring(1, colonIndex).toLowerCase() : null;
    const filter = isTypeSpecified ? fullMentionText.substring(colonIndex + 1) : 
                  fullMentionText.length > 1 ? fullMentionText.substring(1) : '';
    // console.log('type', type);
    // console.log('filter', filter);
    
    const position = atPosition ? {
      x: atPosition.left,
      y: atPosition.bottom + window.scrollY,
      width: atPosition.width,
      height: atPosition.height
    } : {
      x: range.getBoundingClientRect().left,
      y: range.getBoundingClientRect().bottom + window.scrollY,
      width: 1,
      height: range.getBoundingClientRect().height
    };
  
    // Update cursor context
    const newContext = {
      isAtMention: true,
      isTypeSpecified,
      type,
      filter,
      position,
      fullMentionText
    };
    
    if (
      cursorContext.isAtMention !== newContext.isAtMention ||
      cursorContext.isTypeSpecified !== newContext.isTypeSpecified ||
      cursorContext.type !== newContext.type ||
      cursorContext.filter !== newContext.filter ||
      !cursorContext.position ||
      cursorContext.position.x !== newContext.position.x ||
      cursorContext.position.y !== newContext.position.y
    ) {
      setCursorContext(newContext);
      onCurrentMentionChange(isTypeSpecified ? `@${type}` : '@', type, filter);
      // console.log('FULL MENTION TEXT', fullMentionText);
      
      if (onAtMenu) {
        onAtMenu(true, position);
        setTimeout(() => inputRef.current?.focus(), 1);
      }
    }
  }, [cursorContext, onAtMenu, onCurrentMentionChange]);

  const resetCursorContext = () => {
    setCursorContext({
      isAtMention: false,
      isTypeSpecified: false,
      type: null,
      filter: '',
      position: null,
      fullMentionText: null
    });
    onCurrentMentionChange(null, null, '');
    onAtMenu?.(false, null);
  };

  // Handle input changes
  const handleInput = useCallback(() => {
    if (!inputRef.current) return;
    
    const newValue = convertHtmlToValue(inputRef.current.innerHTML);
    onChange(newValue, mentions);
    analyzeCursorContext();
  }, [onChange, mentions, analyzeCursorContext, convertHtmlToValue]);

  // Handle selection changes
  const handleSelectionChange = useCallback(() => {
    analyzeCursorContext();
    
    // Highlight mention pills in selection
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const mentionPills = inputRef.current?.querySelectorAll('.mention-pill');
      mentionPills?.forEach(pill => {
        if (range.intersectsNode(pill)) {
          pill.classList.add('mention-highlight');
        } else {
          pill.classList.remove('mention-highlight');
        }
      });
    }
  }, [analyzeCursorContext]);

  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
    };
  }, [handleSelectionChange]);

  // Handle key events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && onEnterPress) {
      e.preventDefault();
      onEnterPress();
    }
    
    if (e.key === 'Backspace') {
      const selection = window.getSelection();
      if (selection?.rangeCount) {
        const range = selection.getRangeAt(0);
        if (range.collapsed && range.startOffset === 0) {
          const previousNode = range.startContainer.previousSibling;
          if (previousNode?.nodeType === Node.ELEMENT_NODE && 
              (previousNode as Element).classList.contains('mention-pill')) {
            e.preventDefault();
            const mention = {
              id: (previousNode as Element).getAttribute('data-id') || '',
              type: (previousNode as Element).getAttribute('data-type') || '',
              name: (previousNode as Element).getAttribute('data-name') || '',
            };
            previousNode.parentNode?.removeChild(previousNode);
            onMentionRemoved?.(mention);
            handleInput();
          }
        }
      }
    }
  };

  // Insert a mention at current cursor position
  const handleInsertMention = useCallback((mention: Mention) => {
    if (!inputRef.current) return;
  
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
  
    const range = selection.getRangeAt(0);
    const { type, name, id } = mention;
    
    // Create mention pill with proper name handling
    const pillElement = createMentionPillElement(
      { id, type, name: name.trim() }, 
      onMentionClicked
    );
    
    // Delete any partial mention text
    const cursorNode = range.startContainer;
    const cursorOffset = range.startOffset;
    
    if (cursorNode.nodeType === Node.TEXT_NODE) {
      const text = cursorNode.textContent || '';
      const textBeforeCursor = text.substring(0, cursorOffset);
      const atIndex = textBeforeCursor.lastIndexOf('@');
      
      if (atIndex !== -1) {
        // Delete from @ to cursor position
        const deleteRange = document.createRange();
        deleteRange.setStart(cursorNode, atIndex);
        deleteRange.setEnd(cursorNode, cursorOffset);
        deleteRange.deleteContents();
        
        // Adjust the range to start at the @ position
        range.setStart(cursorNode, atIndex);
        range.collapse(true);
      }
    }
  
    // Insert mention pill
    range.insertNode(pillElement);
    
    // Create new range right after the inserted mention
    const newRange = document.createRange();
    newRange.setStartAfter(pillElement);
    newRange.collapse(true);
    
    // Update selection
    selection.removeAllRanges();
    selection.addRange(newRange);

    const newHtml = inputRef.current.innerHTML;
    const newTextValue = convertHtmlToValue(newHtml);
    const newMentions = [...mentions, mention];

    onChange(newTextValue, newMentions);
    setMentions(newMentions);
    onMentionCreated?.(mention);
    resetCursorContext();
    
    // Ensure the input maintains focus
    inputRef.current.focus();
    setTimeout(() => {
      selection.removeAllRanges();
      selection.addRange(newRange);
    }, 0);
  }, [onMentionCreated, onMentionClicked, mentions, onChange, convertHtmlToValue, resetCursorContext]);

  // Insert type specifier at current cursor position
  const insertTypeSpecifier = useCallback((type: string) => {
    if (!inputRef.current) return;
    
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    
    const range = selection.getRangeAt(0);
    const cursorNode = range.startContainer;
    const cursorOffset = range.startOffset;
    
    if (cursorNode.nodeType === Node.TEXT_NODE) {
      const fullText = cursorNode.textContent || '';
      
      // Find the @ position before cursor
      const atIndexBeforeCursor = fullText.substring(0, cursorOffset).lastIndexOf('@');
      if (atIndexBeforeCursor === -1) return;
      
      // Find the end of the mention (either whitespace or end of text)
      let mentionEndIndex = cursorOffset;
      while (mentionEndIndex < fullText.length && !/\s/.test(fullText[mentionEndIndex])) {
        mentionEndIndex++;
      }
      
      // Get the full mention text (from @ to end of mention)
      const mentionText = fullText.substring(atIndexBeforeCursor, mentionEndIndex);
      
      // Check if we already have a type specifier in the full mention
      if (mentionText.indexOf(':') === -1) {
        // No colon yet, insert the type and colon
        const typeText = `${type.toLowerCase()}:`;
        
        // Delete the existing mention text after @
        const deleteRange = document.createRange();
        deleteRange.setStart(cursorNode, atIndexBeforeCursor + 1); // +1 to keep the @
        deleteRange.setEnd(cursorNode, mentionEndIndex);
        deleteRange.deleteContents();
        
        // Insert the type text
        const typeNode = document.createTextNode(typeText);
        range.insertNode(typeNode);
        
        // Move cursor after the inserted text
        const newRange = document.createRange();
        newRange.setStartAfter(typeNode);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);
        
        // Trigger input event to update state
        handleInput();
      }
    }
  }, [handleInput]);

  // Expose insertTypeSpecifier to parent component via ref
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
    insertMention: (mention: Mention) => {
      handleInsertMention(mention);
    },
    insertTypeSpecifier: (type: string) => {
      insertTypeSpecifier(type);
    }
  }));

  // Handle composition events
  const handleCompositionStart = () => setIsComposing(true);
  const handleCompositionEnd = () => {
    setIsComposing(false);
    analyzeCursorContext();
  };

  return (
    <InputWrapper onClick={() => inputRef.current?.focus()}>
      <MentionPillStyles />
      <StyledInput
        ref={inputRef}
        contentEditable
        data-placeholder={placeholder}
        $colorMode={colorMode}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onCompositionStart={handleCompositionStart}
        onCompositionEnd={handleCompositionEnd}
      />
    </InputWrapper>
  );
});

export default InputWithMentions;